import { Stack, Text } from '@mantine/core';
import type { ComponentType } from 'react';
import {
  normalizeIgMessage,
  type IgMessageInput,
  type IgMessageKind,
  type IgMessageView,
} from '../../../lib/instagram-message';
import {
  AttachmentCard,
  BubbleText,
  MediaBubble,
  QuickReplyChips,
  TemplateButtons,
  TemplateCards,
} from '../bubbles';
import { ReferralChip, ReplyQuote, StoryReplyContext } from '../messages/instagram/context-strips';

/**
 * Instagram Direct renderer registry — the Instagram twin of
 * `whatsapp-content.tsx` and `messenger-content.tsx`.
 *
 * Adding a message type is: teach `normalizeIgMessage` its payload, then add a
 * row here. No component parses raw Meta JSON, and anything the normalizer
 * can't classify resolves to the soft note rather than vanishing.
 */

interface IgRendererProps {
  view: IgMessageView;
  incoming: boolean;
}

function IgSoftNote({ view }: IgRendererProps) {
  return (
    <Text size="sm" fs="italic" c="dimmed">
      {view.fallbackLabel}
    </Text>
  );
}

function IgText({ view }: IgRendererProps) {
  return <BubbleText>{view.body ?? view.fallbackLabel}</BubbleText>;
}

function IgMedia({ view }: IgRendererProps) {
  const media = view.media;
  if (!media) return <IgSoftNote view={view} incoming={false} />;
  return <MediaBubble kind={media.kind} url={media.url} alt="Shared media" />;
}

function IgSticker({ view }: IgRendererProps) {
  const url = view.sticker?.url;
  if (!url) return <IgSoftNote view={view} incoming={false} />;
  return (
    <img
      src={url}
      alt="sticker"
      loading="lazy"
      style={{ display: 'block', width: 120, maxWidth: 128, height: 'auto', objectFit: 'contain' }}
    />
  );
}

function IgQuickReplies({ view }: IgRendererProps) {
  return <QuickReplyChips text={view.body} chips={view.quickReplies ?? []} />;
}

function IgCards({ view }: IgRendererProps) {
  return <TemplateCards cards={view.cards ?? []} />;
}

function IgButtons({ view }: IgRendererProps) {
  const b = view.buttons;
  return (
    <>
      {b?.text && <BubbleText>{b.text}</BubbleText>}
      <TemplateButtons buttons={b?.buttons ?? []} />
    </>
  );
}

/** A shared post / reel / story / product — Instagram shows these as a labelled
 *  card, not an inline player; the DM payload carries no playable asset. */
function IgShare({ view }: IgRendererProps) {
  const s = view.share;
  if (!s) return <IgSoftNote view={view} incoming={false} />;
  return <AttachmentCard label={s.label} url={s.url} imageUrl={s.imageUrl ?? undefined} />;
}

function IgReaction({ view }: IgRendererProps) {
  return <Text size="lg">{view.reactionEmoji ?? '👍'}</Text>;
}

function IgItalicNote({ view }: IgRendererProps) {
  return (
    <Text size="sm" fs="italic" c="dimmed">
      {view.body ?? view.fallbackLabel}
    </Text>
  );
}

/** A standalone `messaging_referrals` marker — centred, chrome-less. */
function IgReferralMarker({ view }: IgRendererProps) {
  return (
    <Text size="xs" c="dimmed" fs="italic" ta="center">
      {view.body ?? view.fallbackLabel}
    </Text>
  );
}

const IG_RENDERERS: Record<IgMessageKind, ComponentType<IgRendererProps>> = {
  text: IgText,
  media: IgMedia,
  sticker: IgSticker,
  quick_replies: IgQuickReplies,
  cards: IgCards,
  buttons: IgButtons,
  share: IgShare,
  ephemeral: IgItalicNote,
  reaction: IgReaction,
  deleted: IgItalicNote,
  referral_marker: IgReferralMarker,
  unsupported: IgSoftNote,
};

export function resolveIgRenderer(view: IgMessageView): ComponentType<IgRendererProps> {
  return IG_RENDERERS[view.kind] ?? IgSoftNote;
}

/** The message body only, without context strips. */
export function IgMessageBody({ view, incoming }: IgRendererProps) {
  const Renderer = resolveIgRenderer(view);
  return <Renderer view={view} incoming={incoming} />;
}

/** One Instagram message: any context strips (ad chip / quoted reply / story
 *  reply) above the body. */
export function IgMessageContent({ m, incoming }: { m: IgMessageInput; incoming: boolean }) {
  const view = normalizeIgMessage(m);
  if (!view.capabilities.context) return <IgMessageBody view={view} incoming={incoming} />;
  const { referral, replyTo, storyReply } = view.context;
  return (
    <Stack gap={4}>
      {referral && <ReferralChip referral={referral} />}
      {replyTo && <ReplyQuote replyTo={replyTo} incoming={incoming} />}
      {storyReply && <StoryReplyContext url={storyReply.url} incoming={incoming} />}
      <IgMessageBody view={view} incoming={incoming} />
    </Stack>
  );
}

/**
 * `ChannelCapabilities['renderContent']` seam — the same contract Messenger's
 * registry satisfies, so wiring Instagram onto the shared `ChannelInbox` shell
 * later is a contained change.
 */
export function renderInstagramContent(m: {
  isIncoming: boolean;
  text: string | null;
  messageType: string | null;
  payloadJson?: string | null;
  mediaUrl?: string | null;
  replyTo?: { id: number; preview: string; isIncoming: boolean } | null;
}) {
  return (
    <IgMessageContent
      m={{
        message: m.text,
        messageType: m.messageType,
        payloadJson: m.payloadJson ?? null,
        mediaUrl: m.mediaUrl ?? null,
        replyTo: m.replyTo ?? null,
      }}
      incoming={m.isIncoming}
    />
  );
}
